import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { adminRestaurantsService } from '../../services/adminRestaurants';
import { tablesService, Table } from '../../services/tables';
import { useNavigate } from 'react-router-dom';

type TableNode = {
  id: string;
  xPct: number; // porcentaje X relativo al contenedor (0-100)
  yPct: number; // porcentaje Y relativo al contenedor (0-100)
  label?: string;
};

const STORAGE_KEY = 'admin-table-layout-v1';
const GRID_SIZE_PCT = 2; // snap-to-grid ~2% del contenedor

// Áreas del plano (porcentajes relativos al contenedor)
const AREAS: Record<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza', { x: number; y: number; w: number; h: number; label: string }> = {
  interior: { x: 5, y: 5, w: 55, h: 55, label: 'Interior' },
  terraza:  { x: 65, y: 5, w: 30, h: 40, label: 'Terraza' },
  barra:    { x: 5, y: 65, w: 35, h: 30, label: 'Barra' },
  vip:      { x: 45, y: 65, w: 20, h: 30, label: 'VIP' },
  exterior: { x: 70, y: 50, w: 25, h: 45, label: 'Exterior' },
};

// Normaliza valores de ubicación del backend a claves de área conocidas
const normalizeAreaKey = (raw: any): 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza' => {
  const s = String(raw || '').toLowerCase().trim();
  if (s.includes('terra')) return 'terraza';
  if (s.includes('exter') || s.includes('afuera') || s.includes('patio')) return 'exterior';
  if (s.includes('bar')) return 'barra';
  if (s.includes('vip')) return 'vip';
  if (s.includes('inter')) return 'interior';
  return (['interior','exterior','barra','vip','terraza'].includes(s) ? s : 'interior') as any;
};

const TableLayoutPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [isDraggingId, setIsDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [nodes, setNodes] = useState<TableNode[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [showZones, setShowZones] = useState(true);
  const [nodeSize, setNodeSize] = useState<number>(64);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [selectedArea, setSelectedArea] = useState<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza'>('interior');
  const selectedAreaInitializedRef = useRef(false);
  const [focusArea, setFocusArea] = useState<boolean>(false);
  const [areaMaxSizePx, setAreaMaxSizePx] = useState<Record<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza', number>>({
    interior: Infinity,
    terraza: Infinity,
    barra: Infinity,
    vip: Infinity,
    exterior: Infinity,
  });

  // Cargar desde localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as TableNode[];
        if (Array.isArray(parsed)) setNodes(parsed);
      } else {
        // Estado inicial mínimo: 3 mesas de ejemplo
        setNodes([
          { id: 'T1', xPct: 10, yPct: 20, label: 'Mesa 1' },
          { id: 'T2', xPct: 35, yPct: 40, label: 'Mesa 2' },
          { id: 'T3', xPct: 70, yPct: 60, label: 'Mesa 3' },
        ]);
      }
    } catch {}
  }, []);

  // Cargar desde backend mesas y layout guardado
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  useEffect(() => {
    const loadFromBackend = async () => {
      if (!user?.uid || !selectedRestaurantId) return;
      try {
        const [restaurant, tablesResp] = await Promise.all([
          adminRestaurantsService.getRestaurantById(user.uid, selectedRestaurantId),
          tablesService.getTables(user.uid, selectedRestaurantId)
        ]);
        const layout = (restaurant as any)?.onboarding?.tableLayout as Array<{ id: string; xPct: number; yPct: number; label?: string }> | undefined;
        const tablesList: Table[] = Array.isArray(tablesResp) ? tablesResp : [];
        setTables(tablesList);

        // Clamp a su área
        const clampToArea = (t: Table, xPct: number, yPct: number) => {
          const loc = normalizeAreaKey(t.location);
          const area = AREAS[loc];
          const minX = area.x;
          const maxX = area.x + area.w;
          const minY = area.y;
          const maxY = area.y + area.h;
          return {
            xPct: Math.min(maxX, Math.max(minX, xPct)),
            yPct: Math.min(maxY, Math.max(minY, yPct)),
          };
        };

        // Indices por área para posiciones por defecto consistentes
        const counters: Record<string, number> = { interior: 0, terraza: 0, barra: 0, vip: 0, exterior: 0 };
        const defaultPositions = (t: Table): { xPct: number; yPct: number } => {
          const loc = normalizeAreaKey(t.location);
          const area = AREAS[loc];
          const index = counters[loc]++;
          const cols = 3;
          const row = Math.floor(index / cols);
          const col = index % cols;
          const paddingPct = 5;
          const usableW = Math.max(0, area.w - paddingPct * 2);
          const usableH = Math.max(0, area.h - paddingPct * 2);
          const stepX = cols > 1 ? usableW / (cols - 1) : usableW;
          const stepY = Math.max(10, usableH / 5);
          return {
            xPct: area.x + paddingPct + col * stepX,
            yPct: area.y + paddingPct + row * stepY,
          };
        };

        const mergedNodes: TableNode[] = tablesList.map((t) => {
          const found = layout?.find(n => n.id === t.id);
          if (found && typeof found.xPct === 'number' && typeof found.yPct === 'number') {
            const clamped = clampToArea(t, found.xPct, found.yPct);
            return { id: t.id, xPct: clamped.xPct, yPct: clamped.yPct, label: `${t.number}` };
          }
        
          const pos = defaultPositions(t);
          return { id: t.id, xPct: pos.xPct, yPct: pos.yPct, label: `${t.number}` };
        });
        if (mergedNodes.length > 0) setNodes(mergedNodes);
      } catch {}
    };
    loadFromBackend();
  }, [user?.uid, selectedRestaurantId]);

  const save = useCallback((next: TableNode[]) => {
    setNodes(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => e.preventDefault(), []);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const dragStartPctRef = useRef<{ xPct: number; yPct: number } | null>(null);
  const dragStartCenterPxRef = useRef<{ cx: number; cy: number } | null>(null);
  const hasMovedRef = useRef<boolean>(false);
  const startDrag = useCallback((e: React.PointerEvent, id: string) => {
    if (!containerRef.current) return;
    // const target = e.currentTarget as HTMLElement;
    setIsDraggingId(id);
    // Offset respecto al centro del elemento, para que el cursor "agarre" exacto
    // Forzar agarre pegado al cursor: sin offset
    setDragOffset({ x: 0, y: 0 });
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    const node = nodes.find(n => n.id === id);
    dragStartPctRef.current = node ? { xPct: node.xPct, yPct: node.yPct } : null;
    if (containerRef.current && node) {
      const rect = containerRef.current.getBoundingClientRect();
      dragStartCenterPxRef.current = {
        cx: (node.xPct / 100) * rect.width,
        cy: (node.yPct / 100) * rect.height,
      };
    } else {
      dragStartCenterPxRef.current = null;
    }
    hasMovedRef.current = false;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    if (!isDraggingId || !containerRef.current || !dragOffset) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Ignorar click sin movimiento (evitar saltos)
    if (dragStartPosRef.current) {
      const dx = Math.abs(e.clientX - dragStartPosRef.current.x);
      const dy = Math.abs(e.clientY - dragStartPosRef.current.y);
      if (!hasMovedRef.current && dx + dy < 6) {
        return;
      }
      hasMovedRef.current = true;
    }
    // Calcular centro objetivo pegado al mouse usando el offset de click
    // Si estamos en foco, el contenedor muestra solo el área, pero seguimos guardando % globales
    // Para evitar desfase, mapear el cursor a % globales usando el área seleccionada
    let centerX = e.clientX - rect.left - (dragOffset?.x || 0);
    let centerY = e.clientY - rect.top - (dragOffset?.y || 0);
    if (focusArea && isDraggingId) {
      const t = tables.find(t => t.id === isDraggingId);
      if (t) {
        const loc = normalizeAreaKey(t.location);
        const a = AREAS[loc];
        // Convertir desde px del contenedor (área enfocada visualmente) a % globales
        const xLocalPct = (centerX / rect.width) * 100; // 0..100 dentro del contenedor
        const yLocalPct = (centerY / rect.height) * 100;
        // Mapear a coordenadas globales del canvas
        centerX = ((a.x + (xLocalPct * a.w) / 100) / 100) * rect.width;
        centerY = ((a.y + (yLocalPct * a.h) / 100) / 100) * rect.height;
      }
    }
    let xPct = Math.min(100, Math.max(0, (centerX / rect.width) * 100));
    let yPct = Math.min(100, Math.max(0, (centerY / rect.height) * 100));

    // No clamping durante el drag en foco para evitar desplazamiento visual
    const table = tables.find(t => t.id === isDraggingId);
    if (table && !focusArea) {
      const loc = (table.location as keyof typeof AREAS) || 'interior';
      const area = AREAS[loc];
      const minX = area.x;
      const maxX = area.x + area.w;
      const minY = area.y;
      const maxY = area.y + area.h;
      xPct = Math.min(maxX, Math.max(minX, xPct));
      yPct = Math.min(maxY, Math.max(minY, yPct));
    }

    setNodes(prev => prev.map(n => n.id === isDraggingId ? { ...n, xPct, yPct } : n));
  }, [isDraggingId, dragOffset, tables]);

  const handlePointerUp = useCallback(() => {
    if (!isDraggingId) return;
    const snap = (vPct: number) => Math.round(vPct / GRID_SIZE_PCT) * GRID_SIZE_PCT;
    // Aplicar snap al finalizar
    const rect = containerRef.current?.getBoundingClientRect();
    let adjusted = nodes;
    if (rect && dragStartPctRef.current) {
      const moved = nodes.find(n => n.id === isDraggingId);
      if (moved) {
        let xPct = snap(moved.xPct);
        let yPct = snap(moved.yPct);
        // Clamp a su área con normalización robusta
        const t = tables.find(t => t.id === moved.id);
        if (t) {
          const loc = normalizeAreaKey(t.location);
          const a = AREAS[loc];
          xPct = Math.min(a.x + a.w, Math.max(a.x, xPct));
          yPct = Math.min(a.y + a.h, Math.max(a.y, yPct));
        }
        adjusted = nodes.map(n => n.id === moved.id ? { ...n, xPct, yPct } : n);
      }
    }
    setIsDraggingId(null);
    setDragOffset(null);
    dragStartPosRef.current = null;
    dragStartPctRef.current = null;
    dragStartCenterPxRef.current = null;
    hasMovedRef.current = false;
    // Persistir
    save(adjusted);
    // Ya no guardamos en backend automáticamente; solo con botón Guardar
  }, [isDraggingId, nodes, save]);

  const goToManageTables = useCallback(() => {
    navigate('/admin/tables');
  }, [navigate]);

  const containerClasses = useMemo(() => (
    'relative w-full h-[65vh] md:h-[70vh] rounded-2xl border border-[#22B6B2]/30 bg-[url(/assets/images/ots/area.png)] bg-cover bg-center overflow-hidden'
  ), []);

  // Calcular tamaño de mesa responsive según ancho del contenedor
  useEffect(() => {
    const recalc = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Tamaño adaptativo para móvil: mayor en pantallas pequeñas
      let factor = 0.06;
      if (rect.width <= 360) factor = 0.18;
      else if (rect.width <= 480) factor = 0.14;
      else if (rect.width <= 640) factor = 0.10;
      const size = Math.max(40, Math.min(96, Math.round(rect.width * factor)));
      setNodeSize(size);
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      if (mobile) setFocusArea(true);
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, []);

  // Ocultar zonas por defecto en pantallas pequeñas
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) setShowZones(false);
    }
  }, []);

  // Asegurar un área seleccionada solo una vez al cargar mesas por primera vez
  useEffect(() => {
    if (tables.length > 0 && !selectedAreaInitializedRef.current) {
      const firstLoc = String((tables[0].location as any) || 'interior').toLowerCase();
      if (['interior','exterior','barra','vip','terraza'].includes(firstLoc)) {
        setSelectedArea(firstLoc as any);
      }
      selectedAreaInitializedRef.current = true;
    }
  }, [tables]);

  // Re-cargar listado de mesas al cambiar de área en foco (por si hay cambios concurrentes o filtros)
  useEffect(() => {
    const refetchTables = async () => {
      if (!user?.uid || !selectedRestaurantId) return;
      try {
        const tablesResp = await tablesService.getTables(user.uid, selectedRestaurantId);
        const tablesList: Table[] = Array.isArray(tablesResp) ? tablesResp : [];
        setTables(tablesList);
      } catch {}
    };
    if (focusArea) {
      refetchTables();
    }
  }, [focusArea, selectedArea, user?.uid, selectedRestaurantId]);

  // Si estamos en foco y el área seleccionada no tiene mesas, cambiar a la primera área con mesas
  useEffect(() => {
    if (!focusArea) return;
    const areas: Array<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza'> = ['interior', 'terraza', 'barra', 'vip', 'exterior'];
    const hasInSelected = nodes.some(n => {
      const loc = String((tables.find(t => t.id === n.id)?.location as any) || '').toLowerCase();
      return loc === selectedArea;
    });
    if (!hasInSelected) {
      const withNodes = areas.find(a => nodes.some(n => {
        const loc = String((tables.find(t => t.id === n.id)?.location as any) || '').toLowerCase();
        return loc === a;
      }));
      if (withNodes) setSelectedArea(withNodes);
    }
  }, [focusArea, selectedArea, nodes, tables]);

  // Calcular el tamaño máximo permitido por área para que quepan todas las mesas visibles
  useEffect(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    const calcForArea = (areaKey: 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza', visibleNodes: TableNode[]) => {
      const areaCfg = AREAS[areaKey];
      // Tamaño del área en px (si focusArea, el área ocupa todo el contenedor)
      const areaWidthPx = focusArea && selectedArea === areaKey ? rect.width : (rect.width * (areaCfg.w / 100));
      const areaHeightPx = focusArea && selectedArea === areaKey ? rect.height : (rect.height * (areaCfg.h / 100));

      const count = visibleNodes.length;
      if (count <= 0 || areaWidthPx <= 0 || areaHeightPx <= 0) return Infinity;

      // Aproximar grilla ideal según proporción del área
      const aspect = areaWidthPx / areaHeightPx;
      let cols = Math.ceil(Math.sqrt(count * aspect));
      cols = Math.max(1, cols);
      const rows = Math.max(1, Math.ceil(count / cols));

      const cellW = areaWidthPx / cols;
      const cellH = areaHeightPx / rows;
      const maxSize = Math.max(24, Math.min(cellW, cellH) * 0.9); // margen del 10%
      return maxSize;
    };

    const areas: Array<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza'> = ['interior', 'terraza', 'barra', 'vip', 'exterior'];
    const next: any = {};
    areas.forEach((key) => {
      // nodos visibles en esa área según modo
      const nodesInArea = (focusArea ? nodes.filter(n => {
        const loc = normalizeAreaKey(tables.find(tt => tt.id === n.id)?.location);
        return loc === selectedArea && selectedArea === key;
      }) : nodes.filter(n => {
        const loc = normalizeAreaKey(tables.find(tt => tt.id === n.id)?.location);
        return loc === key;
      }));

      next[key] = calcForArea(key, nodesInArea);
    });

    setAreaMaxSizePx(next as Record<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza', number>);
  }, [nodes, tables, focusArea, selectedArea, isMobile, nodeSize]);

  return (
    <div className="min-h-screen bg-[#e9ecef] p-5">
      <div className="flex items-center justify-center mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setShowZones(s => !s)}
            className="px-4 py-2 rounded-lg bg-[#f7ebd6] text-[#2C4358] font-semibold border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:bg-[#f0e0c0]"
          >
            {showZones ? 'Ocultar áreas' : 'Mostrar áreas'}
          </button>
          <button
            onClick={goToManageTables}
            className="px-4 py-2 rounded-lg bg-[#22B6B2] text-white font-semibold hover:opacity-90"
          >
            Gestionar mesas
          </button>
          <button
            onClick={async () => {
              save(nodes);
              if (user?.uid && selectedRestaurantId) {
                try {
                  // Guardar posiciones por tableId
                  const toSave = nodes
                    .filter(n => tables.some(t => t.id === n.id))
                    .map(n => ({ id: n.id, xPct: n.xPct, yPct: n.yPct }));
                  await adminRestaurantsService.updateRestaurant(user.uid, selectedRestaurantId, {
                    onboarding: { tableLayout: toSave }
                  });
                } catch (e) {}
              }
            }}
            className="px-4 py-2 rounded-lg bg-[#2C4358] text-white font-semibold hover:opacity-90"
          >
            Guardar
          </button>
        </div>
      </div>

      {/* Selector de áreas en móvil */}
      {isMobile && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {Object.entries(AREAS).map(([key, area]) => (
              <button
                key={key}
                onClick={() => { setSelectedArea(key as any); setFocusArea(true); selectedAreaInitializedRef.current = true; }}
                className={`px-3 py-2 rounded-full text-xs font-bold border transition-colors whitespace-nowrap ${selectedArea === key ? 'bg-[#22B6B2] text-white border-[#22B6B2]' : 'bg-white text-[#2C4358] border-[#22B6B2]/30'}`}
              >
                {area.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selector de áreas en desktop con modo foco */}
      {!isMobile && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex gap-2 flex-wrap">
            {Object.entries(AREAS).map(([key, area]) => (
              <button
                key={key}
                onClick={() => { setSelectedArea(key as any); setFocusArea(true); selectedAreaInitializedRef.current = true; }}
                className={`px-3 py-2 rounded-full text-xs font-bold border transition-colors whitespace-nowrap ${selectedArea === key && focusArea ? 'bg-[#22B6B2] text-white border-[#22B6B2]' : 'bg-white text-[#2C4358] border-[#22B6B2]/30'}`}
              >
                {area.label}
              </button>
            ))}
          </div>
          {focusArea && (
            <button
              onClick={() => setFocusArea(false)}
              className="flex-shrink-0 px-3 py-2 rounded-full text-xs font-bold bg-[#6B7280] text-white"
              title="Salir de foco de área"
            >
              Salir foco
            </button>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className={focusArea 
          ? 'relative w-full h-[75vh] rounded-2xl border border-[#22B6B2]/30 bg-[url(/assets/images/ots/area.png)] bg-cover bg-center overflow-hidden' 
          : containerClasses}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{ touchAction: 'none' }}
      >
        {showZones && !focusArea && (
          <>
            {Object.entries(AREAS).map(([key, area]) => (
              <div
                key={key}
                className="absolute border-2 border-dashed border-[#22B6B2]/30 bg-white/5"
                style={{ left: `${area.x}%`, top: `${area.y}%`, width: `${area.w}%`, height: `${area.h}%` }}
              >
                <div className="absolute -top-6 left-0 px-2 py-0.5 rounded-md bg-[#22B6B2] text-white text-xs font-bold shadow">
                  {area.label}
                </div>
              </div>
            ))}
          </>
        )}
        {focusArea && (
          <div className="absolute top-2 left-2 z-10 bg-[#22B6B2] text-white text-xs font-bold px-2 py-1 rounded">
            {AREAS[selectedArea].label}
          </div>
        )}
        {focusArea && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              zIndex: 0,
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(34,182,178,0.08) 0, rgba(34,182,178,0.08) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(34,182,178,0.08) 0, rgba(34,182,178,0.08) 1px, transparent 1px, transparent 40px)",
              backgroundSize: '40px 40px',
            }}
          />
        )}
        {focusArea ? (
          (() => {
            const a = AREAS[selectedArea];
            let areaNodes = nodes.filter(n => normalizeAreaKey(tables.find(tt => tt.id === n.id)?.location) === selectedArea);
            if (areaNodes.length === 0) {
              // Fallback flexible por si viene un valor no mapeado
              const hint = selectedArea.slice(0, 4);
              areaNodes = nodes.filter(n => {
                const raw = String((tables.find(tt => tt.id === n.id)?.location as any) || '').toLowerCase();
                return raw.includes(hint);
              });
            }
            return (
              <div className="absolute inset-0" style={{ zIndex: 1 }}>
                {areaNodes.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center select-none" style={{ transform: 'none' }}>
                    <div className="text-xs font-bold text-[#85929e] bg-white/80 px-3 py-2 rounded-lg border border-[#22B6B2]/30">Sin mesas en esta área</div>
                  </div>
                )}
                {areaNodes.map(node => {
                  const table = tables.find(t => t.id === node.id);
                  // Escala visual por capacidad: <=2 chica, 4 mediana (base), >=6 grande, >=8 extra grande
                  const getVisualScale = (capacity?: number) => {
                    const c = Number(capacity || 4);
                    if (c <= 2) return 0.8;     // chica
                    if (c <= 3) return 0.9;     // intermedia
                    if (c <= 5) return 1.1;     // mediana+
                    if (c <= 7) return 1.2;     // grande (>=6 y <=7)
                    return 1.3;                 // extra grande (>=8)
                  };
                  const scale = getVisualScale(table?.capacity);
                  let scaledSize = Math.round(nodeSize * scale);
                  // fuentes dependen del tamaño final calculado
                  const locKey = (table?.location as keyof typeof AREAS) || 'interior';
                  // Limitar por tamaño máximo permitido del área calculado dinámicamente
                  const maxAllowed = areaMaxSizePx[locKey] || Infinity;
                  scaledSize = Math.min(scaledSize, Math.round(maxAllowed));
                  const finalLabelFont = Math.max(9, Math.round(scaledSize * 0.18));
                  const finalCapFont = Math.max(8, Math.round(scaledSize * 0.16));
                  // Posiciones normalizadas dentro del área en porcentaje
                  const localX = Math.min(100, Math.max(0, ((node.xPct - a.x) / a.w) * 100));
                  const localY = Math.min(100, Math.max(0, ((node.yPct - a.y) / a.h) * 100));
                  return (
                    <div
                      key={node.id}
                      onPointerDown={handlePointerDown}
                      onPointerEnter={(e) => e.preventDefault()}
                      onPointerDownCapture={(e) => startDrag(e, node.id)}
                      className="absolute touch-none select-none -translate-x-1/2 -translate-y-1/2"
                      style={{
                        left: `${localX}%`,
                        top: `${localY}%`,
                        width: scaledSize,
                        height: scaledSize,
                        cursor: isDraggingId === node.id ? 'grabbing' : 'grab',
                      }}
                    >
                      <div className={`relative w-full h-full rounded-xl border ${isDraggingId === node.id ? 'border-[#22B6B2]' : 'border-transparent'}`}>
                        <img
                          src="/assets/images/mesita.png"
                          alt={node.label || node.id}
                          className="w-full h-full object-contain"
                          draggable={false}
                        />
                        <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm rounded-full px-2 py-0.5 font-bold text-[#2C4358] border border-[#22B6B2]/30 shadow-sm"
                          style={{ fontSize: finalLabelFont }}
                        >
                          #{node.label || node.id}
                        </div>
                        {table && (
                          <div className="absolute left-1/2 top-full -translate-x-1/2 mt-1 text-[#85929e] font-semibold whitespace-nowrap"
                            style={{ fontSize: finalCapFont }}
                          >
                            {table.capacity} personas
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : (
          nodes.map(node => {
          const table = tables.find(t => t.id === node.id);
          // Escala visual por capacidad: <=2 chica, 4 mediana (base), >=6 grande, >=8 extra grande
          const getVisualScale = (capacity?: number) => {
            const c = Number(capacity || 4);
            if (c <= 2) return 0.8;     // chica
            if (c <= 3) return 0.9;     // intermedia
            if (c <= 5) return 1.1;     // mediana+
            if (c <= 7) return 1.2;     // grande (>=6 y <=7)
            return 1.3;                 // extra grande (>=8)
          };
          const scale = getVisualScale(table?.capacity);
          let scaledSize = Math.round(nodeSize * scale);
          // fuentes dependen del tamaño final calculado
          let locKey = normalizeAreaKey(table?.location);
          // Limitar por tamaño máximo permitido del área calculado dinámicamente
          const maxAllowed = areaMaxSizePx[locKey] || Infinity;
          scaledSize = Math.min(scaledSize, Math.round(maxAllowed));
          const finalLabelFont = Math.max(9, Math.round(scaledSize * 0.18));
          const finalCapFont = Math.max(8, Math.round(scaledSize * 0.16));
          return (
            <div
              key={node.id}
              onPointerDown={handlePointerDown}
              onPointerEnter={(e) => e.preventDefault()}
              onPointerDownCapture={(e) => startDrag(e, node.id)}
              className="absolute touch-none select-none -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${node.xPct}%`,
                top: `${node.yPct}%`,
                width: scaledSize,
                height: scaledSize,
                cursor: isDraggingId === node.id ? 'grabbing' : 'grab',
              }}
            >
              <div className={`relative w-full h-full rounded-xl border ${isDraggingId === node.id ? 'border-[#22B6B2]' : 'border-transparent'}`}>
                <img
                  src="/assets/images/mesita.png"
                  alt={node.label || node.id}
                  className="w-full h-full object-contain"
                  draggable={false}
                />
                <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm rounded-full px-2 py-0.5 font-bold text-[#2C4358] border border-[#22B6B2]/30 shadow-sm"
                  style={{ fontSize: finalLabelFont }}
                >
                  #{node.label || node.id}
                </div>
                {table && (
                  <div className="absolute left-1/2 top-full -translate-x-1/2 mt-1 text-[#85929e] font-semibold whitespace-nowrap"
                    style={{ fontSize: finalCapFont }}
                  >
                    {table.capacity} personas
                  </div>
                )}
              </div>
            </div>
          );
        })
        )}
      </div>
      <p className="text-xs text-[#85929e] mt-2">Arrastra las mesas para posicionarlas y cuando estén en su lugar, haz click en el botón de "Guardar cambios".</p>
    </div>
  );
};

export default TableLayoutPage;
